import React, { useState, useMemo } from "react";
import { Box, IconButton, Button, Switch, Chip } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { DataTable, TableToolbar, normalizeList } from "components/shared";
import {
  useGetNotificationsQuery,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
} from "state/api";

// Audience is not a status enum, so it doesn't belong in statusPalette/StatusChip.
// Map each target to a theme-semantic Chip color so the three read distinctly.
const TARGET_COLOR = { all: "info", customers: "success", staff: "warning" };

const NotificationTable = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isFetching } = useGetNotificationsQuery({ search });
  const [updateNotification] = useUpdateNotificationMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  // The notifications endpoint returns the full list (no server-side paging),
  // so slice client-side to keep the pagination controls honest.
  const { rows, total } = useMemo(() => {
    const { rows: allRows, total: count } = normalizeList(data, {
      page: page + 1,
      pageSize: rowsPerPage,
    });
    const start = page * rowsPerPage;
    return { rows: allRows.slice(start, start + rowsPerPage), total: count };
  }, [data, page, rowsPerPage]);

  const toggleStatus = (row) =>
    updateNotification({
      id: row._id,
      status: row.status === "active" ? "inactive" : "active",
    });
  const resend = (row) => updateNotification({ id: row._id, resend: true });
  const remove = (row) => deleteNotification(row._id);

  const columns = useMemo(
    () => [
      {
        field: "sl",
        headerName: "SL",
        render: (_row, i) => page * rowsPerPage + i + 1,
      },
      { field: "title", headerName: "Title" },
      { field: "description", headerName: "Description" },
      {
        field: "target",
        headerName: "Target",
        render: (row) => {
          const label = row.target
            ? row.target.charAt(0).toUpperCase() + row.target.slice(1)
            : "—";
          return (
            <Chip
              label={label}
              size="small"
              variant="outlined"
              color={TARGET_COLOR[row.target] || "default"}
            />
          );
        },
      },
      { field: "sentCount", headerName: "Sent Count", align: "right" },
      {
        field: "status",
        headerName: "Status",
        render: (row) => (
          <Switch
            checked={row.status === "active"}
            onChange={() => toggleStatus(row)}
          />
        ),
      },
      {
        field: "resend",
        headerName: "Resend",
        render: (row) => (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => resend(row)}
          >
            Resend
          </Button>
        ),
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        render: (row) => (
          <IconButton color="error" onClick={() => remove(row)}>
            <Delete fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, rowsPerPage]
  );

  return (
    <Box sx={{ mt: 3 }}>
      <TableToolbar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search by title…"
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No notifications sent yet"
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default NotificationTable;
